import { useEffect } from 'react';
import { isLoggedIn } from '@saviynt/common';
import { MS_REDIRECT_URL } from 'ui-common/src/constants';
import { useQuery } from '../misc/hooks';

// token_type is a readable (non-HttpOnly) cookie signalling an established
// session before the redux store has hydrated. Mirrors App's hasAuthSession so
// the redirect can also fire during the store-hydration window.
const hasAuthSession = () =>
  document.cookie.split(';').some((c) => c.trim().startsWith('token_type='));

/**
 * Restores a deep link the user requested before logging in (UI-1504).
 *
 * Runs once the user is fully authenticated (past MFA and the /ECM/login/init
 * session hand-off), i.e. when they land back in the app (typically the Home
 * page). ecmLogin()/logout() save the requested URL in sessionStorage — which
 * survives the /ECM/logout/clearSession hop, unlike the cookie — and this hook
 * consumes it.
 *
 * The value is cleared only AFTER the redirect has succeeded: it is kept while
 * navigating and dropped on the next load, once we have arrived at the target
 * (target === current) or find the value invalid/stale. Clearing before a
 * full-page replace can't be tied to success (the page unloads immediately);
 * confirming arrival on the target page can. This keeps it consumed exactly once
 * and prevents redirect loops.
 */
export default function usePostLoginRedirect() {
  const query = useQuery();

  useEffect(() => {
    if (query.get('jwt') || !(isLoggedIn() || hasAuthSession())) {
      return;
    }

    let saved = null;

    try {
      saved = sessionStorage.getItem(MS_REDIRECT_URL);
    } catch (e) {
      saved = null;
    }

    if (!saved) {
      return;
    }

    let target = null;

    try {
      const url = new URL(saved, window.location.origin);

      // Same-origin ECMv6 app routes only; never an auth route (avoids a loop).
      if (
        url.origin === window.location.origin &&
        url.pathname.startsWith('/ECMv6/') &&
        !url.pathname.startsWith('/ECMv6/auth/')
      ) {
        target = `${url.pathname}${url.search}${url.hash}`;
      }
    } catch (e) {
      // Malformed saved value — leave target null so it is cleared below.
    }

    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (target && target !== current) {
      // Not there yet: navigate and KEEP the key so the next load can confirm
      // arrival and clear it. Do not clear here — the redirect may not happen.
      window.location.replace(target);

      return;
    }

    // Arrived at the target (redirect succeeded) or the value was invalid/stale:
    // consume it now so the deep link is used exactly once.
    try {
      sessionStorage.removeItem(MS_REDIRECT_URL);
    } catch (e) {
      // ignore
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount
}
